import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import api from '../services/api';

const ReplyPage = () => {
  const { messageId } = useParams();
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchContext = async () => {
      try {
        const res = await api.get(`/messages/${messageId}/context`);
        setContext(res.data);
      } catch {
        setContext(null);
      } finally {
        setLoading(false);
      }
    };
    fetchContext();
  }, [messageId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !message.trim()) {
      toast({ title: 'Error', description: 'Please fill in all fields', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      await api.post(`/messages/${messageId}/reply`, { email, message });
      setSent(true);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to send reply', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!context) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <Card className="p-8 max-w-md w-full text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">This message could not be found or the link has expired.</p>
          <Link to="/contacts">
            <Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" /> Contact Us</Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <Card className="p-8 max-w-md w-full text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Reply Sent!</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Thank you for your reply. We'll get back to you shortly.</p>
          <Link to="/">
            <Button className="bg-purple-600 hover:bg-purple-700">Back to Home</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-lg mx-auto">
        <Link to="/" className="inline-flex items-center text-sm text-purple-600 hover:text-purple-700 mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to AllSEO Conferences
        </Link>

        <Card className="p-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            Reply to AllSEO Conferences
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Re: {context.subject}
          </p>

          {context.lastReply && (
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 mb-6 border-l-4 border-purple-500">
              <p className="text-xs text-gray-500 mb-2 font-medium">Our last message to you:</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{context.lastReply}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="reply-email">Your Email *</Label>
              <Input
                id="reply-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="reply-email-input"
              />
            </div>
            <div>
              <Label htmlFor="reply-message">Your Message *</Label>
              <Textarea
                id="reply-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your reply here..."
                rows={5}
                required
                data-testid="reply-message-input"
              />
            </div>
            <Button
              type="submit"
              disabled={sending}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="reply-submit-btn"
            >
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : 'Send Reply'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ReplyPage;
